const { useEffect, useState, useRef } = require("react");

const useMRZ = (onScanComplete, pauseScanner, scanAnalytics) => {
  const [scanStatus, setScanStatus] = useState(null);
  const hasShownPartialMessage = useRef(false);
  const readyForNextScan = useRef(true);
  const hasMarkedResult = useRef(false);

  const validateRequiredFields = (data) => {
    if (!data?.parsed?.fields) return false;

    const requiredFields = {
      documentNumber: data.parsed.fields.documentNumber,
      issuingState: data.parsed.fields.issuingState,
      sex: data.parsed.fields.sex,
      birthDate: data.parsed.fields.birthDate,
    };

    return Object.entries(requiredFields).every(([field, value]) => {
      const isValid = value !== null && value !== undefined && value !== "";
      if (!isValid) {
        console.log(`Missing or invalid required field: ${field}`);
      }
      return isValid;
    });
  };

  useEffect(() => {
    const receiveMessage = (evt) => {
      if (pauseScanner.current) {
        return;
      }

      if (scanStatus === "partial" && !hasShownPartialMessage.current) {
        hasShownPartialMessage.current = true;
        const timer = setTimeout(() => {
          hasShownPartialMessage.current = false;
        }, 5000);
        return () => clearTimeout(timer);
      }

      if (evt.data?.action === "SENDING_OCR_DATA") {
        const { payload } = evt.data;

        // Mark as ready for next scan since processing is complete
        if (payload?.processingComplete) {
          readyForNextScan.current = true;
        }

        if (payload?.ocrData?.scanStatus === "progress") {
          return;
        }

        if (payload?.ocrData?.data?.error === "no roi found") {
          setScanStatus("no roi found");
        } else if (
          payload?.ocrData?.data?.error &&
          !payload?.ocrData?.data?.parsed
        ) {
          setScanStatus("partial");
        } else if (!payload?.ocrData?.data?.error) {
          const isValid = validateRequiredFields(payload?.ocrData?.data);

          // Mark first ROI detected
          if (scanAnalytics && scanAnalytics.markRoiDetected) {
            scanAnalytics.markRoiDetected(isValid);
          }

          if (isValid) {
            setScanStatus("success");
            if (
              scanAnalytics &&
              scanAnalytics.markSuccessfulResultDetected &&
              !hasMarkedResult.current
            ) {
              scanAnalytics.markSuccessfulResultDetected();
              hasMarkedResult.current = true;
            }
            setTimeout(() => {
              onScanComplete?.(payload?.ocrData?.data);
            }, 1800);
          } else {
            setScanStatus("partial");
          }
        }

        // Notify the component that we're ready for the next scan
        window.postMessage(
          { action: "READY_FOR_NEXT_SCAN" },
          window.location.origin
        );
      }
    };

    window.addEventListener("message", receiveMessage, false);
    return () => {
      window.removeEventListener("message", receiveMessage, false);
    };
  }, [scanStatus, onScanComplete, scanAnalytics, pauseScanner]);

  return { scanStatus, readyForNextScan };
};

export default useMRZ;
